import { API_URL } from "../utils/common";
import FetchClient from "./FetchClient";

export interface Category {
  categoryId: string;
  name: string;
  categoryImage?: string; // Keep optional for backward compatibility
}

export interface Subcategory {
  name: string;
  id: string;
}

export const WALMART_DEFAULT_CATEGORY_MAX_PAGES = 20;

export interface CategoryV2 {
  rootKey: string;
  subcategories: Subcategory[];
  /** Per-root Walmart browse page cap; unset uses WALMART_DEFAULT_CATEGORY_MAX_PAGES. */
  maxPages?: number | null;
  /** Ultima default path (EST): before noon; null/undefined = run. */
  runMorning?: boolean | null;
  /** Ultima default path (EST): noon onward; null/undefined = run. */
  runAfternoon?: boolean | null;
}

export type PatchRootCategoryBody = {
  maxPages?: number | null;
  runMorning?: boolean | null;
  runAfternoon?: boolean | null;
};

// Home Depot Categories interfaces (same structure as CategoryV2)
export interface HomeDepotCategory {
  rootKey: string;
  subcategories: Subcategory[];
  maxPages?: number | null;
  runMorning?: boolean | null;
  runAfternoon?: boolean | null;
}

export interface UltimaHomeDepotCategory {
  title: string;
  n: string;
  /** Ultima HD default path (EST): before noon; null/undefined = legacy/default behavior. */
  runMorning?: boolean | null;
  /** Ultima HD default path (EST): noon onward; null/undefined = legacy/default behavior. */
  runAfternoon?: boolean | null;
}

export async function getCategories(): Promise<Category[]> {
  const res = await FetchClient.get<unknown>("/categories");

  // Check different possible response structures
  let categories: Category[] = [];

  if (res && typeof res === "object" && "data" in res) {
    const resData = res as { data?: unknown };
    if (
      resData.data &&
      typeof resData.data === "object" &&
      "data" in resData.data
    ) {
      const nestedData = resData.data as { data?: unknown };
      if (Array.isArray(nestedData.data)) {
        categories = nestedData.data;
      }
    } else if (Array.isArray(resData.data)) {
      categories = resData.data;
    }
  } else if (Array.isArray(res)) {
    categories = res as Category[];
  }

  return categories;
}

// Admin-specific functions for category management
export async function getAdminCategories(): Promise<Category[]> {
  const res = await FetchClient.get<unknown>("/admin/categories");

  // Check different possible response structures
  let categories: Category[] = [];

  if (res && typeof res === "object" && "data" in res) {
    const resData = res as { data?: unknown };
    if (
      resData.data &&
      typeof resData.data === "object" &&
      "data" in resData.data
    ) {
      const nestedData = resData.data as { data?: unknown };
      if (Array.isArray(nestedData.data)) {
        categories = nestedData.data;
      }
    } else if (Array.isArray(resData.data)) {
      categories = resData.data;
    }
  } else if (Array.isArray(res)) {
    categories = res as Category[];
  }

  return categories;
}

export async function addAdminCategory(category: {
  name: string;
  categoryId: string;
}): Promise<void> {
  await FetchClient.post<void>("/admin/categories", category);
}

export async function deleteAdminCategory(categoryId: string): Promise<void> {
  await FetchClient.delete<void>(`/admin/categories?categoryId=${categoryId}`);
}

export async function updateAdminCategory(
  categoryId: string,
  category: {
    name?: string;
  }
): Promise<void> {
  await FetchClient.patch<void>(
    `/admin/categories?categoryId=${categoryId}`,
    category
  );
}

export async function checkCategoryInStore(
  categoryId: string,
  storeId: string
): Promise<{ exists: boolean; totalItems?: number; categoryTitle?: string }> {
  const res = await FetchClient.get<{
    data: {
      exists: boolean;
      totalItems?: number;
      categoryTitle?: string;
    };
    status: number;
  }>(`/admin/categories/check?categoryId=${categoryId}&storeId=${storeId}`);

  return res.data;
}

export async function getTargetCategories(): Promise<CategoryV2[]> {
  const res = await FetchClient.get<unknown>("/categories-target");

  // Check different possible response structures
  let categories: CategoryV2[] = [];

  if (res && typeof res === "object" && "data" in res) {
    const resData = res as { data?: unknown };
    if (
      resData.data &&
      typeof resData.data === "object" &&
      "data" in resData.data
    ) {
      const nestedData = resData.data as { data?: unknown };
      if (Array.isArray(nestedData.data)) {
        categories = nestedData.data;
      }
    } else if (Array.isArray(resData.data)) {
      categories = resData.data;
    }
  } else if (Array.isArray(res)) {
    categories = res as CategoryV2[];
  }

  return categories;
}

export async function getDollarGeneralCategories(): Promise<CategoryV2[]> {
  const res = await FetchClient.get<unknown>("/categories-dollar-general");
  let categories: CategoryV2[] = [];
  if (res && typeof res === "object" && "data" in res) {
    const resData = res as { data?: unknown };
    categories = resData.data as CategoryV2[];
  }
  return categories;
}

// CategoriesV2 functions
export async function getCategoriesV2(): Promise<CategoryV2[]> {
  const res = await FetchClient.get<unknown>("/categoriesV2");

  // Check different possible response structures
  let categories: CategoryV2[] = [];

  if (res && typeof res === "object" && "data" in res) {
    const resData = res as { data?: unknown };
    if (
      resData.data &&
      typeof resData.data === "object" &&
      "data" in resData.data
    ) {
      const nestedData = resData.data as { data?: unknown };
      if (Array.isArray(nestedData.data)) {
        categories = nestedData.data;
      }
    } else if (Array.isArray(resData.data)) {
      categories = resData.data;
    }
  } else if (Array.isArray(res)) {
    categories = res as CategoryV2[];
  }

  return categories;
}

export async function getAdminCategoriesV2(): Promise<CategoryV2[]> {
  const res = await FetchClient.get<unknown>("/admin/categoriesV2");

  // Check different possible response structures
  let categories: CategoryV2[] = [];

  if (res && typeof res === "object" && "data" in res) {
    const resData = res as { data?: unknown };
    if (
      resData.data &&
      typeof resData.data === "object" &&
      "data" in resData.data
    ) {
      const nestedData = resData.data as { data?: unknown };
      if (Array.isArray(nestedData.data)) {
        categories = nestedData.data;
      }
    } else if (Array.isArray(resData.data)) {
      categories = resData.data;
    }
  } else if (Array.isArray(res)) {
    categories = res as CategoryV2[];
  }

  return categories;
}

export async function addSubcategory(
  rootKey: string,
  subcategory: { name: string; id: string }
): Promise<void> {
  await FetchClient.post<void>(
    `/admin/categoriesV2/${rootKey}/subcategories`,
    subcategory
  );
}

export async function updateSubcategory(
  rootKey: string,
  subcategoryId: string,
  subcategory: { name?: string }
): Promise<void> {
  await FetchClient.patch<void>(
    `/admin/categoriesV2/${rootKey}/subcategories/${subcategoryId}`,
    subcategory
  );
}

export async function deleteSubcategory(
  rootKey: string,
  subcategoryId: string
): Promise<void> {
  await FetchClient.delete<void>(
    `/admin/categoriesV2/${rootKey}/subcategories/${subcategoryId}`
  );
}

export async function addRootCategory(rootKey: string): Promise<void> {
  await FetchClient.post<void>("/admin/categoriesV2", { rootKey });
}

export async function deleteRootCategory(rootKey: string): Promise<void> {
  await FetchClient.delete<void>(`/admin/categoriesV2/${rootKey}`);
}

export async function patchRootCategoryFields(
  rootKey: string,
  body: PatchRootCategoryBody
): Promise<void> {
  await FetchClient.patch<void>(
    `/admin/categoriesV2/${encodeURIComponent(rootKey)}`,
    body
  );
}

// Helper function to flatten categoriesV2 to the old Category format for backward compatibility
export function flattenCategoriesV2(categoriesV2: CategoryV2[]): Category[] {
  const flattened: Category[] = [];

  categoriesV2.forEach((rootCategory) => {
    rootCategory.subcategories.forEach((subcategory) => {
      flattened.push({
        categoryId: subcategory.id,
        name: subcategory.name,
      });
    });
  });

  return flattened;
}

// Legacy functions (keep for backward compatibility)
export function deleteCategory(categoryId: string): Promise<void> {
  return fetch(`${API_URL}/categories?categoryId=${categoryId}`, {
    method: "DELETE",
  }).then((res) => {
    if (!res.ok) {
      throw new Error("Failed to delete category");
    }
  });
}

export function addCategory(category: {
  name: string;
  categoryId: string;
}): Promise<Category> {
  return fetch(`${API_URL}/categories`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(category),
  }).then((res) => {
    if (!res.ok) {
      throw new Error("Failed to add category");
    }
    return res.json();
  });
}

// Home Depot Categories functions
export async function getHomeDepotCategories(): Promise<HomeDepotCategory[]> {
  const res = await FetchClient.get<unknown>("/categories-home-depot");

  // Check different possible response structures
  let categories: HomeDepotCategory[] = [];

  if (res && typeof res === "object" && "data" in res) {
    const resData = res as { data?: unknown };
    if (
      resData.data &&
      typeof resData.data === "object" &&
      "data" in resData.data
    ) {
      const nestedData = resData.data as { data?: unknown };
      if (Array.isArray(nestedData.data)) {
        categories = nestedData.data;
      }
    } else if (Array.isArray(resData.data)) {
      categories = resData.data;
    }
  } else if (Array.isArray(res)) {
    categories = res as HomeDepotCategory[];
  }

  return categories;
}

export async function getAdminHomeDepotCategories(): Promise<
  HomeDepotCategory[]
> {
  const res = await FetchClient.get<unknown>("/admin/categories-home-depot");

  // Check different possible response structures
  let categories: HomeDepotCategory[] = [];

  if (res && typeof res === "object" && "data" in res) {
    const resData = res as { data?: unknown };
    if (
      resData.data &&
      typeof resData.data === "object" &&
      "data" in resData.data
    ) {
      const nestedData = resData.data as { data?: unknown };
      if (Array.isArray(nestedData.data)) {
        categories = nestedData.data;
      }
    } else if (Array.isArray(resData.data)) {
      categories = resData.data;
    }
  } else if (Array.isArray(res)) {
    categories = res as HomeDepotCategory[];
  }

  return categories;
}

export async function addHomeDepotSubcategory(
  rootKey: string,
  subcategory: { name: string; id: string }
): Promise<void> {
  await FetchClient.post<void>(
    `/admin/categories-home-depot/${rootKey}/subcategories`,
    subcategory
  );
}

export async function updateHomeDepotSubcategory(
  rootKey: string,
  subcategoryId: string,
  subcategory: { name?: string }
): Promise<void> {
  await FetchClient.patch<void>(
    `/admin/categories-home-depot/${rootKey}/subcategories/${subcategoryId}`,
    subcategory
  );
}

export async function deleteHomeDepotSubcategory(
  rootKey: string,
  subcategoryId: string
): Promise<void> {
  await FetchClient.delete<void>(
    `/admin/categories-home-depot/${rootKey}/subcategories/${subcategoryId}`
  );
}

export async function getAdminUltimaHomeDepotCategories(): Promise<
  UltimaHomeDepotCategory[]
> {
  const res = await FetchClient.get<unknown>("/admin/ultima-hd-categories");
  let categories: UltimaHomeDepotCategory[] = [];

  if (res && typeof res === "object" && "data" in res) {
    const resData = res as { data?: unknown };
    if (Array.isArray(resData.data)) {
      categories = resData.data as UltimaHomeDepotCategory[];
    }
  } else if (Array.isArray(res)) {
    categories = res as UltimaHomeDepotCategory[];
  }

  return categories;
}

export async function patchUltimaHomeDepotCategorySchedule(
  categoryId: string,
  body: Pick<PatchRootCategoryBody, "runMorning" | "runAfternoon">
): Promise<void> {
  await FetchClient.patch<void>(
    `/admin/ultima-hd-categories/${encodeURIComponent(categoryId)}`,
    body
  );
}

export interface UltimaAmazonCategory {
  id: string;
  label: string;
  url: string;
}

export type UltimaAmazonCategoryCheckResult = {
  valid: boolean;
  filteredUrl: string;
  totalResults: number;
  estimatedPages: number;
  sampleProductName?: string;
  message?: string;
};

export async function checkUltimaAmazonCategory(
  url: string
): Promise<UltimaAmazonCategoryCheckResult> {
  const res = await FetchClient.post<unknown>("/admin/ultima-amazon-categories/check", {
    url,
  });
  if (res && typeof res === "object" && "data" in res) {
    const d = (res as { data: unknown }).data;
    if (d && typeof d === "object") {
      return d as UltimaAmazonCategoryCheckResult;
    }
  }
  throw new Error("Unexpected response from Amazon category check");
}

export async function getAdminUltimaAmazonCategories(): Promise<UltimaAmazonCategory[]> {
  const res = await FetchClient.get<unknown>("/admin/ultima-amazon-categories");
  let categories: UltimaAmazonCategory[] = [];
  if (res && typeof res === "object" && "data" in res) {
    const resData = res as { data?: unknown };
    if (Array.isArray(resData.data)) {
      categories = resData.data as UltimaAmazonCategory[];
    }
  } else if (Array.isArray(res)) {
    categories = res as UltimaAmazonCategory[];
  }
  return categories;
}

export async function postUltimaAmazonCategory(c: UltimaAmazonCategory): Promise<void> {
  await FetchClient.post<void>("/admin/ultima-amazon-categories", c);
}

export async function patchUltimaAmazonCategory(
  categoryId: string,
  body: Partial<Pick<UltimaAmazonCategory, "id" | "label" | "url">>
): Promise<void> {
  await FetchClient.patch<void>(
    `/admin/ultima-amazon-categories/${encodeURIComponent(categoryId)}`,
    body
  );
}

export async function deleteUltimaAmazonCategory(categoryId: string): Promise<void> {
  await FetchClient.delete<void>(
    `/admin/ultima-amazon-categories/${encodeURIComponent(categoryId)}`
  );
}
